import { useCallback } from "react";
import { type RowSelectionState } from "@tanstack/react-table";

import { useStore } from "@nanostores/react";
import { type ProspectResponse } from "@vega-api/generated/types/prospectResponse";

import { DEFAULT_TIER_LEVEL } from "../constants/prospectConstants";
import {
  $selectedProspectsData,
  type ProspectWithFormData,
} from "../stores/prospectsStore";

const buildUpdatedData = (
  newValue: RowSelectionState,
  selectedProspectsData: Record<string, ProspectWithFormData>,
  prospectsMap: Map<string, ProspectResponse>,
): Record<string, ProspectWithFormData> => {
  const currentPageIds = new Set(prospectsMap.keys());

  const selectionsFromOtherPages = Object.fromEntries(
    Object.entries(selectedProspectsData).filter(
      ([id]) => !currentPageIds.has(id),
    ),
  );

  const currentPageSelections = Object.entries(newValue).reduce(
    (acc, [id, isSelected]) => {
      if (!isSelected) return acc;

      if (id in selectedProspectsData) {
        acc[id] = selectedProspectsData[id];

        return acc;
      }

      const prospect = prospectsMap.get(id);

      if (!prospect) return acc;

      acc[id] = {
        ...prospect,
        tierLevel: DEFAULT_TIER_LEVEL,
        notes: "",
        reasonForInclusion: [],
        estimatedTicketSizeMin: undefined,
        estimatedTicketSizeMax: undefined,
      };

      return acc;
    },
    {} as Record<string, ProspectWithFormData>,
  );

  return { ...selectionsFromOtherPages, ...currentPageSelections };
};

export const useProspectsSelection = ({
  prospectsData,
}: {
  prospectsData: ProspectResponse[];
}) => {
  const selectedProspectsData = useStore($selectedProspectsData);

  const selectedRows: RowSelectionState = Object.keys(
    selectedProspectsData,
  ).reduce((acc, id) => ({ ...acc, [id]: true }), {} as RowSelectionState);

  const handleRowSelectionChange = useCallback(
    (
      updater:
        | RowSelectionState
        | ((old: RowSelectionState) => RowSelectionState),
    ) => {
      const prospectsMap = new Map(
        prospectsData.map((p) => [p.limitedPartnerId, p]),
      );

      const newValue =
        typeof updater === "function" ? updater(selectedRows) : updater;

      const updatedData = buildUpdatedData(
        newValue,
        selectedProspectsData,
        prospectsMap,
      );

      $selectedProspectsData.set(updatedData);
    },
    [selectedRows, selectedProspectsData, prospectsData],
  );

  const handleClearSelection = useCallback(() => {
    $selectedProspectsData.set({});
  }, []);

  const handleRemoveFromSelection = useCallback((limitedPartnerId: string) => {
    const currentData = $selectedProspectsData.get();
    // legit use of unused item because we are removing it
    // eslint-disable-next-line unused-imports/no-unused-vars
    const { [limitedPartnerId]: _, ...newData } = currentData;
    $selectedProspectsData.set(newData);
  }, []);

  const selectedCount = Object.keys(selectedProspectsData).length;

  return {
    handleClearSelection,
    handleRemoveFromSelection,
    handleRowSelectionChange,
    selectedCount,
    selectedProspects: Object.values(selectedProspectsData),
    selectedRows,
  };
};
